import { useRef, useEffect } from 'react';
import { useChatStore } from '../../store/chatStore';
import { Message } from './Message';
import { TypingIndicator } from './TypingIndicator';

/**
 * Displays the scrollable list of chat messages with auto-scroll behavior
 * and a typing indicator shown while the assistant is generating a response.
 */
export function MessageList() {
  const { messages, isLoading, loadingStatus, sessionId } = useChatStore();
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);
  const prevSessionIdRef = useRef(sessionId);

  // Reset scroll flag when session changes so we jump instantly on load
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      hasScrolledRef.current = false;
      prevSessionIdRef.current = sessionId;
    }
  }, [sessionId]);

  // Auto-scroll. Two cases:
  //   - While waiting (user just sent / typing indicator): follow the bottom.
  //   - When an ANSWER arrives: anchor to the TOP of that answer, so the
  //     user reads from its first line. Scrolling to scrollHeight here was
  //     the "jumps to the previous Q&A" bug — the height was measured before
  //     mermaid diagrams rendered, so the late growth left the viewport
  //     stranded above the answer. Anchoring to the message top does not
  //     depend on the height of anything below it.
  useEffect(() => {
    requestAnimationFrame(() => {
      if (!containerRef.current) return;
      if (!hasScrolledRef.current) {
        // First render or session switch — jump to bottom instantly
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        hasScrolledRef.current = true;
        return;
      }
      const last = messages[messages.length - 1];
      const answered =
        last && last.role === 'assistant' && !last.isLoading && last.content.trim().length > 0;
      if (answered && lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
      } else {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
  }, [messages, isLoading]);

  // Filter out placeholder assistant messages (empty content with isLoading)
  // to avoid duplicate "Assistant" labels when TypingIndicator is showing
  const visibleMessages = messages.filter((msg) => {
    // Show all user messages
    if (msg.role === 'user') return true;

    // Show assistant messages that have content or are not in loading state
    return msg.content && msg.content.trim().length > 0;
  });

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto px-4 py-3"
      role="log"
      aria-label="Chat messages"
      aria-live="polite"
    >
      <div className="max-w-3xl mx-auto space-y-3">
        {visibleMessages.map((message, idx) => (
          <div
            key={message.id}
            ref={idx === visibleMessages.length - 1 ? lastMessageRef : undefined}
            style={{ scrollMarginTop: '8px' }}
          >
            <Message message={message} />
          </div>
        ))}

        {isLoading && (
          <TypingIndicator status={loadingStatus} />
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
